import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1 } from "iconsax-react";
import cn from "classnames";
import { Button, ModalForm } from "@/shared/ui";
import { useAppSelector } from "@/shared/hooks";
import { SECTION_WORK_FORMAT, SECTION_WORK_SCHEDULE } from "@/features/hr/amendments/consts";
import { replacePlaceholders } from "@/features/hr/amendments/utils";
import { buildContractTemplateValues, renderWorkScheduleText } from "@/features/hr/contracts/utils";
import type { ContractClause, ContractDetailResponse } from "@/features/hr/contracts/types";
import type { WorkerListItem } from "@/features/hr/employees";

const ContractTemplateView = lazy(
  () => import("@/features/hr/contracts/pages/FillContractPage/components/ContractTemplateView"),
);

function blankPlaceholders(text: string): string {
  return text.replace(/\{\{\s*\w+\s*\}\}/g, "_____________");
}

interface Props {
  clauses: ContractClause[];
  contractData?: ContractDetailResponse;
  employee?: WorkerListItem;
  isWorkScheduleClauseDisabled?: boolean;
  onClauseClick: (clause: ContractClause) => void;
  onBack: () => void;
}

export default function ContractClausesView({
  clauses,
  contractData,
  employee,
  isWorkScheduleClauseDisabled = false,
  onClauseClick,
  onBack,
}: Props) {
  const { t } = useTranslation("FillContractPage");
  const userData = useAppSelector((state) => state.auth.data?.user);

  const workScheduleTextKk = contractData ? renderWorkScheduleText(contractData, "kk") : "_____________";
  const workScheduleTextRu = contractData ? renderWorkScheduleText(contractData, "ru") : "_____________";

  const templateValues = buildContractTemplateValues({
    data: contractData ?? {},
    organization: userData?.organization,
    cityNameRu: contractData?.work_city?.name_ru,
    employee: {
      fullName: employee?.full_name,
      iin: employee?.iin,
      phone: employee?.contacts?.phone,
    },
  });

  function compareSectionNumbers(a: string, b: string): number {
    const aParts = a.split(".").map((part) => parseInt(part, 10));
    const bParts = b.split(".").map((part) => parseInt(part, 10));

    const maxLength = Math.max(aParts.length, bParts.length);

    for (let i = 0; i < maxLength; i++) {
      const aPart = aParts[i] ?? 0;
      const bPart = bParts[i] ?? 0;

      if (aPart !== bPart) {
        return aPart - bPart;
      }
    }

    return 0;
  }

  function groupClausesBySection(clauses: ContractClause[]): Record<string, ContractClause[]> {
    const grouped: Record<string, ContractClause[]> = {};
    clauses.forEach((clause) => {
      const sectionNumber = clause.section_number.split(".")[0];
      if (!grouped[sectionNumber]) {
        grouped[sectionNumber] = [];
      }
      grouped[sectionNumber].push(clause);
    });
    Object.keys(grouped).forEach((section) => {
      grouped[section].sort((a, b) => compareSectionNumbers(a.section_number, b.section_number));
    });
    return grouped;
  }

  const groupedClauses = groupClausesBySection(clauses);

  const sectionTitles: Record<string, { kk: string; ru: string }> = {
    "1": { kk: "1. ШАРТТЫҢ МӘНІ", ru: "1. ПРЕДМЕТ ДОГОВОРА" },
    "2": {
      kk: "2. ЖҰМЫС БЕРУШІНІҢ ҚҰҚЫҚТАРЫ МЕН МІНДЕТТЕРІ",
      ru: "2. ПРАВА И ОБЯЗАННОСТИ РАБОТОДАТЕЛЯ",
    },
    "3": {
      kk: "3. ЖҰМЫСКЕРДІҢ ҚҰҚЫҚТАРЫ МЕН МІНДЕТТЕРІ",
      ru: "3. ПРАВА И ОБЯЗАННОСТИ РАБОТНИКА",
    },
    "4": {
      kk: "4. ЖҰМЫС УАҚЫТЫ, ДЕМАЛЫС УАҚЫТЫ ЖӘНЕ ЕҢБЕККЕ АҚЫ ТӨЛЕУ",
      ru: "4. РАБОЧЕЕ ВРЕМЯ, ВРЕМЯ ОТДЫХА И ОПЛАТА ТРУДА",
    },
    "5": {
      kk: "5. ШАРТТЫҢ ҚОЛДАНЫЛУ МЕРЗІМІ, ОНЫ ӨЗГЕРТУ, ТОЛЫҚТЫРУ ЖӘНЕ ТОҚТАТУ ТӘРТІБІ",
      ru: "5. СРОК ДЕЙСТВИЯ ДОГОВОРА, ПОРЯДОК ЕГО ИЗМЕНЕНИЯ, ДОПОЛНЕНИЯ И ПРЕКРАЩЕНИЯ",
    },
    "6": { kk: "6. КЕПІЛДІКТЕР МЕН ӨТЕМАҚЫЛАР", ru: "6. ГАРАНТИИ И КОМПЕНСАЦИИ" },
    "7": {
      kk: "7. ТАРАПТАРДЫҢ ЖАУАПКЕРШІЛІГІ ЖӘНЕ ДАУЛАРДЫ ШЕШУ ТӘРТІБІ",
      ru: "7. ОТВЕТСТВЕННОСТЬ СТОРОН И ПОРЯДОК РАЗРЕШЕНИЯ СПОРОВ",
    },
    "8": {
      kk: "8. СЫБАЙЛАС ЖЕМҚОРЛЫҚҚА ҚАРСЫ ЕРЕЖЕЛЕР",
      ru: "8. АНТИКОРРУПЦИОННЫЕ ПОЛОЖЕНИЯ",
    },
    "9": { kk: "9. ӨЗГЕ ДЕ ТАЛАПТАР", ru: "9. ПРОЧИЕ УСЛОВИЯ" },
  };

  function renderSection(sectionNumber: string) {
    const sectionClauses = groupedClauses[sectionNumber] || [];
    const title = sectionTitles[sectionNumber];

    if (!title) return null;

    function isDisabledClause(sectionNum: string): boolean {
      return sectionNum === SECTION_WORK_FORMAT || (isWorkScheduleClauseDisabled && sectionNum === SECTION_WORK_SCHEDULE);
    }

    return (
      <tr key={sectionNumber}>
        <td className="p-4 border surface-base-stroke align-top">
          <h2 className="text-xl font-bold text-center mb-4">{title.kk}</h2>
          <div>
            {sectionClauses.map((clause) => {
              let contentKk: string;
              if (clause.section_number === SECTION_WORK_SCHEDULE) {
                contentKk = workScheduleTextKk;
              } else if (contractData) {
                contentKk = replacePlaceholders(clause.content_kk, contractData, "kk", {
                  userData: userData
                    ? {
                        organization: userData.organization
                          ? {
                              address_ru: userData.organization.address_ru,
                              address_kk: userData.organization.address_kk,
                            }
                          : undefined,
                      }
                    : undefined,
                  t,
                });
              } else {
                contentKk = blankPlaceholders(clause.content_kk);
              }
              const disabled = isDisabledClause(clause.section_number);
              function handleClauseClick() {
                if (disabled) return;
                onClauseClick(clause);
              }
              return (
                <p
                  key={clause.section_number}
                  className={cn("mb-2 whitespace-pre-line", disabled ? "" : "cursor-pointer hover:underline")}
                  onClick={handleClauseClick}>
                  <strong>{clause.section_number}.</strong> {contentKk}
                </p>
              );
            })}
          </div>
        </td>
        <td className="p-4 border surface-base-stroke align-top">
          <h2 className="text-xl font-bold text-center mb-4">{title.ru}</h2>
          <div>
            {sectionClauses.map((clause) => {
              let contentRu: string;
              if (clause.section_number === SECTION_WORK_SCHEDULE) {
                contentRu = workScheduleTextRu;
              } else if (contractData) {
                contentRu = replacePlaceholders(clause.content_ru, contractData, "ru", {
                  userData: userData
                    ? {
                        organization: userData.organization
                          ? {
                              address_ru: userData.organization.address_ru,
                              address_kk: userData.organization.address_kk,
                            }
                          : undefined,
                      }
                    : undefined,
                  t,
                });
              } else {
                contentRu = blankPlaceholders(clause.content_ru);
              }
              const disabled = isDisabledClause(clause.section_number);
              function handleClauseClick() {
                if (disabled) return;
                onClauseClick(clause);
              }
              return (
                <p
                  key={clause.section_number}
                  className={cn("mb-2 whitespace-pre-line", disabled ? "" : "cursor-pointer hover:underline")}
                  onClick={handleClauseClick}>
                  <strong>{clause.section_number}.</strong> {contentRu}
                </p>
              );
            })}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <ModalForm
      icon={DocumentText1}
      onClose={onBack}
      hasBackground={false}
      resize={true}
      footer={
        <Button variant="tertiary" size="huge" className="w-full" onClick={onBack}>
          {t("buttons.back")}
        </Button>
      }>
      <div className="flex flex-col justify-between p-1 h-full">
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{t("contractChanges.changeOtherConditions.title")}</h4>
          <p className="text-body-regular-sm content-base-secondary">
            {t("contractChanges.changeOtherConditions.subtitle")}
          </p>
        </div>

        <div className="flex-1 overflow-auto page-scroll pr-5 pt-5">
          <div className="flex flex-col text-body-regular-md content-base-primary surface-base-fill">
            <table className="w-full border-collapse mb-6">
              <tbody>
                <Suspense fallback={null}>
                  <ContractTemplateView values={templateValues} salaryType="fixed" part="intro" />
                </Suspense>

                {renderSection("1")}
                {renderSection("2")}
                {renderSection("3")}
                {renderSection("4")}
                {renderSection("5")}
                {renderSection("6")}
                {renderSection("7")}
                {renderSection("8")}
                {renderSection("9")}

                <Suspense fallback={null}>
                  <ContractTemplateView values={templateValues} salaryType="fixed" part="closing" />
                </Suspense>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </ModalForm>
  );
}

